import * as React from "react";
import { ChartsContainer } from "@mui/x-charts/ChartsContainer";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight"; // adds line where mouse is
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { LineHighlightPlot } from "@mui/x-charts/LineChart"; // adds dot where mouse is

import { DayLines, UncertaintyArea } from "./plotUtil";
import { round } from "./util";


// IDEAS: add continuous color? https://mui.com/x/react-charts/legend/
// Favicon: https://www.vexels.com/png-svg/preview/285289/wing-foil-people-silhouette
// ICO converter https://www.icoconverter.com/

function KelvinToCelsius(array) {
  let conversion = 273.15;
  let q25 = array.data[0].map(function (x) {
    return x - conversion;
  });
  let q50 = array.data[1].map(function (x) {
    return x - conversion;
  });
  let q75 = array.data[2].map(function (x) {
    return x - conversion;
  });
  return [q25, q50, q75];
}

function SunConverter(array) {
  let sunhours = array.data[0].map(function (x) {
    return x / 3600;
  });
  return sunhours;
}

function PrecipConverter(array) {
  let precip = array.data[0].map(function (x) {
    return round(x, 1);
  });
  return precip;
}

export default function WeatherPlot({ t2m, precip, sun, twater }) {
  const forecast_date = t2m.coords.valid_time.data.map(
    (date) => new Date(date),
  );
  const [airtemp_q25, airtemp_q50, airtemp_q75] = KelvinToCelsius(t2m);
  const sunhours = SunConverter(sun);
  const watertemp = twater.data_vars.laketemp.data.map(function (x) {
    return round(x, 1);
  });
  const precipitation = PrecipConverter(precip);
  const id = React.useId();
  const clipPathId = `${id}-clip-path`;
  const temperaturFormatter = (value) => `${round(value, 1)} °C`;
  const precipFormatter = (value) => `${round(value, 1)} mm`;
  const sunFormatter = (value) => `${round(value, 1)} h`;
  const midnight = forecast_date.filter((element) => element.getHours() === 0);
  const current_time = Date.now();
  const plot_center_1 =
    (Math.min(...airtemp_q25) + Math.max(...airtemp_q75)) / 2;
  const min_water = Math.min(...watertemp);
  const min_air = Math.min(...airtemp_q25);
  if ( min_air < min_water ) {
    var temp_min = min_air;
  } else {
    var temp_min = min_water;
  }
  const max_air = Math.max(...airtemp_q75);
  const max_water = Math.max(...airtemp_q75);
  if ( max_air > max_water ) {
    var temp_max = max_air;
  } else {
    var temp_max = max_water;
  }

  // console.log(current_time);
  //   const night_start = forecast_date.filter(element => element.getHours() === 20);
  //console.log(plot_center_1);
  return (
    <>
      <ChartsContainer
        sx={{ minWidth: 1235, mb: -2, ml: -2 }}
        series={[
          {
            yAxisId: "leftAxis",
            type: "line",
            data: airtemp_q50,
            showMark: false,
            label: "Lufttemperatur",
            valueFormatter: temperaturFormatter,
            color: "#ff7b3e",
          },
          {
            yAxisId: "leftAxis",
            type: "line",
            data: watertemp,
            showMark: false,
            label: "Wassertemperatur",
            valueFormatter: temperaturFormatter,
            color: "#34e8f5",
            connectNulls: true,
          },
        ]}
        xAxis={[
          {
            data: forecast_date,
            scaleType: "time",
            valueFormatter: (date, context) =>
              context.location === "tick"
                ? `${date.toLocaleDateString("de-DE", {
                    weekday: "short",
                    hour: "2-digit",
                  })}`
                : `${date.toLocaleDateString("de-DE", {
                    weekday: "long",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                  })} LT`,
            //label: "Temperatur",
            min: forecast_date.at(0),
            max: forecast_date.at(-1),
          },
        ]}
        yAxis={[
          {
            id: "leftAxis",
            label: "Temperatur (°C)",
            width: 50,
            position: "left",
            min: temp_min,
            max: temp_max,
          },
          {
            id: "rightAxis",
            label: "Druckdifferenz (hPa)",
            width: 50,
            position: "right",
          },
        ]}
        height={180}
      >
        <ChartsXAxis />
        {/*<ChartsYAxis axisId="rightAxis" />*/}
        <ChartsYAxis axisId="leftAxis" />
        <g clipPath={`url(#${clipPathId})`}>
          <DayLines midnight={midnight} current_time={current_time} />
          <UncertaintyArea
            lower={airtemp_q25}
            upper={airtemp_q75}
            color={"#ff91001d"}
          />
          <ChartsReferenceLine
            y={round(plot_center_1, 0)}
            lineStyle={{ stroke: "#ffffff09" }}
            labelStyle={{ fontWeight: "bold", opacity: 0.5 }}
            label={`Datenquelle: MeteoSchweiz & Alplakes`}
          />
          <LinePlot />
        </g>
        <ChartsGrid horizontal="true" />
        <MarkPlot />
        <LineHighlightPlot />
        <ChartsAxisHighlight x="line" />
        <ChartsTooltip />
        {/* <ChartsClipPath id={clipPathId} /> */}
      </ChartsContainer>
      <ChartsContainer
        sx={{ minWidth: 1235, ml: -2 }}
        series={[
          {
            yAxisId: "leftAxis",
            type: "bar",
            data: sunhours,
            showMark: true,
            label: "Sonnenschein",
            valueFormatter: sunFormatter,
            color: "#f7e92f",
          },
          {
            yAxisId: "rightAxis",
            type: "bar",
            data: precipitation,
            showMark: false,
            label: "Niederschlag",
            valueFormatter: precipFormatter,
            color: "#6cb8ff",
          },
        ]}
        xAxis={[
          {
            data: forecast_date,
            scaleType: "band",
            valueFormatter: (date, context) =>
              context.location === "tick"
                ? `${date.toLocaleDateString("de-DE", {
                    weekday: "short",
                    hour: "2-digit",
                  })}`
                : `${date.toLocaleDateString("de-DE", {
                    weekday: "long",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                  })} LT`,
            min: forecast_date.at(0),
            max: forecast_date.at(-1),
          },
        ]}
        yAxis={[
          {
            id: "leftAxis",
            label: "Sonnenschein (h)",
            width: 50,
            position: "left",
            max: 1,
            min: 0,
          },
          {
            id: "rightAxis",
            label: "Niederschlag (mm)",
            width: 50,
            position: "right",
            domainLimit: "strict",
            min: 0,
          },

        ]}
        height={180}
      >
        <ChartsXAxis />
        <ChartsYAxis axisId="leftAxis" />
        <ChartsYAxis axisId="rightAxis" />
        <g clipPath={`url(#${clipPathId})`}>
          <DayLines midnight={midnight} current_time={current_time} />
          <ChartsReferenceLine
            y={0.5}
            lineStyle={{ stroke: "#ffffff09" }}
            labelStyle={{ fontWeight: "bold", opacity: 0.5 }}
            label={`Datenquelle: MeteoSchweiz`}
          />
          <BarPlot />
        </g>
        <ChartsGrid horizontal="true" />
        <MarkPlot />
        <LineHighlightPlot />
        <ChartsAxisHighlight x="line" />
        <ChartsTooltip />
        {/* <ChartsClipPath id={clipPathId} /> */}
      </ChartsContainer>
    </>
  );
}
